import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Heart,
  ShoppingCart,
  ChevronDown,
  Menu,
  Loader2,
  LogOut,
  X,
  Truck,
} from "lucide-react";
import {
  useCart,
  useWishlist,
  useCategoriesWithProducts,
} from "../../hooks/useSupabase";
import { getProducts } from "../../services/products.service";
import {
  navigateToSubCategory,
  navigateToCategory,
} from "../../utils/navigation";
import { useAuth } from "../../hooks/useAuth";
import {
  getSupabaseCategoryImageUrl,
  DEFAULT_CATEGORY_ICON,
  getPrimaryProductImageUrl,
} from "../../utils/imageUtils";

const MobileHeader = () => {
  const navigate = useNavigate();
  const { user, signOut, userId } = useAuth();
  const DEMO_USER_ID = userId;

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Supabase hooks
  const { categoriesWithSubs } = useCategoriesWithProducts();
  const { cartCount, loading: cartLoading } = useCart(DEMO_USER_ID);
  const { wishlistCount, loading: wishlistLoading } = useWishlist();

  // Use categories data directly
  const displayCategories = categoriesWithSubs || [];

  // Close mobile menus when screen size changes
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
        setIsMobileSearchOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Search effect
  useEffect(() => {
    if (searchQuery.length <= 2) {
      if (searchLoading) setSearchLoading(false);
      setShowSearchResults(false);
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    const delayedSearch = setTimeout(() => {
      const q = searchQuery.trim();
      if (!q) {
        setShowSearchResults(false);
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }

      getProducts({ search: q, limit: 20, offset: 0 })
        .then(({ products }) => {
          setSearchResults(products || []);
          setShowSearchResults(true);
        })
        .catch(() => {
          setSearchResults([]);
          setShowSearchResults(false);
        })
        .finally(() => {
          setSearchLoading(false);
        });
    }, 300);

    return () => {
      clearTimeout(delayedSearch);
      setSearchLoading(false);
    };
  }, [searchQuery]);

  const handleWishlistClick = () => {
    if (!user) {
      navigate("/registration");
      return;
    }
    navigate("/wishlist");
  };

  const handleCartClick = () => {
    if (!user) {
      navigate("/registration");
      return;
    }
    navigate("/cart");
  };

  const handleTrackingClick = () => {
    if (!user) {
      navigate("/registration");
      return;
    }
    navigate("/tracking");
  };

  const handleSearchEnter = async (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter" && searchQuery.length > 2) {
      setShowSearchResults(false);
      navigate(
        `/products?search=${encodeURIComponent(searchQuery)}&sort=popular`,
        {
          state: {
            searchQuery: searchQuery,
            sort: "popular",
          },
        },
      );
      setSearchQuery("");
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div className="w-full bg-neutral-900 shadow-md md:hidden">
      {/* Mobile Header */}
      <div className="py-3 px-4 border-b border-neutral-800">
        {/* First Row: Logo and Action Buttons */}
        <div className="flex items-center justify-between">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="flex items-center justify-center w-10 h-10 rounded-xl hover:bg-neutral-800 transition-colors"
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>

          {/* Logo - Simplified */}
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/assets/Images/logo/blackstore.png"
              alt="BlackStore"
              className="w-10 h-10 rounded-lg object-cover"
            />
            <h1 className="text-lg font-bold text-white">BlackStore</h1>
          </Link>

          {/* Action Buttons (Wishlist, Cart, Tracking) */}
          <div className="flex items-center space-x-2">
            {/* Wishlist */}
            <div className="relative">
              <button
                onClick={handleWishlistClick}
                className="flex items-center justify-center w-10 h-10 rounded-xl hover:bg-neutral-800 transition-colors relative group"
                aria-label="Wishlist"
              >
                <Heart className="w-5 h-5 text-white group-hover:text-primary transition-colors" />
                {wishlistLoading ? (
                  <div className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center">
                    <Loader2 className="w-3 h-3 animate-spin text-neutral-400" />
                  </div>
                ) : wishlistCount > 0 ? (
                  <span className="absolute -top-1 -right-1 bg-primary text-black rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                    {wishlistCount}
                  </span>
                ) : null}
              </button>
            </div>

            {/* Shopping Cart */}
            <button
              onClick={handleCartClick}
              className="flex items-center justify-center w-10 h-10 rounded-xl hover:bg-neutral-800 transition-colors relative group"
              aria-label="Shopping cart"
            >
              <ShoppingCart className="w-5 h-5 text-white group-hover:text-primary transition-colors" />
              {cartLoading ? (
                <div className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center">
                  <Loader2 className="w-3 h-3 animate-spin text-neutral-400" />
                </div>
              ) : cartCount > 0 ? (
                <span className="absolute -top-1 -right-1 bg-primary text-black text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              ) : null}
            </button>

            {/* Tracking */}
            <button
              onClick={handleTrackingClick}
              className="flex items-center justify-center w-10 h-10 rounded-xl hover:bg-neutral-800 transition-colors relative group"
              aria-label="Track order"
            >
              <Truck className="w-5 h-5 text-white group-hover:text-primary transition-colors" />
            </button>
          </div>
        </div>

        {/* Second Row: Search Bar */}
        <div className="mt-3">
          <div className="relative group">
            <input
              type="text"
              placeholder="Search for products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 pr-12 border-2 border-neutral-700 bg-neutral-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white placeholder:text-neutral-500 transition-all"
              onFocus={() => searchQuery && setShowSearchResults(true)}
              onKeyDown={handleSearchEnter}
              autoFocus={isMobileSearchOpen}
            />
            <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-linear-to-r from-primary-dark to-primary text-black p-2.5 rounded-lg hover:from-primary-darker hover:to-primary-dark transition-all shadow-md">
              {searchLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Mobile Search Results */}
          {showSearchResults && (
            <div className="mt-2 bg-neutral-900 border border-neutral-700 shadow-xl rounded-lg max-h-96 overflow-y-auto">
              <div className="p-2">
                {searchResults.slice(0, 5).map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center p-3 hover:bg-neutral-800 rounded-lg cursor-pointer transition-colors"
                    onClick={() => {
                      setShowSearchResults(false);
                      setSearchQuery("");
                      setIsMobileSearchOpen(false);
                      navigate(`/product/${product.id}`);
                    }}
                  >
                    <img
                      src={
                        getPrimaryProductImageUrl(product.image_paths || []) ||
                        "/assets/images/no-image.png"
                      }
                      alt={product.name}
                      className="w-12 h-12 object-cover rounded-md mr-3"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "/assets/images/no-image.png";
                      }}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-white">
                        {product.name}
                      </div>
                      <div className="text-sm text-neutral-400">
                        {product.category?.name}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 z-40 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed top-0 left-0 h-full w-80 bg-neutral-900 z-50 overflow-y-auto shadow-2xl border-r border-neutral-800">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-4 border-b border-neutral-800 bg-neutral-900">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-neutral-800 rounded-lg flex items-center justify-center overflow-hidden">
                <a href="/">
                  <img
                    src="/assets/Images/logo/blackstore.png"
                    alt="blackstore logo"
                    className="w-full h-full object-cover"
                  />
                </a>
              </div>
              <div>
                <h2 className="font-bold text-lg text-white">BlackStore</h2>
                <p className="text-xs text-neutral-400">Premium Shopping</p>
              </div>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 hover:bg-neutral-800 rounded-lg transition-colors text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Mobile Menu Content */}
          <div className="p-4">
            {/* Quick Links */}
            <div className="mb-6">
              <h3 className="font-semibold text-white mb-3">Quick Links</h3>
              <div className="space-y-2">
                <Link
                  to="/products"
                  className="block py-2 text-neutral-300 hover:text-primary transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  All Products
                </Link>
                <Link
                  to="/products?sort=newest"
                  className="block py-2 text-neutral-300 hover:text-primary transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  New Arrivals
                </Link>
                <Link
                  to="/products?sort=popular"
                  className="block py-2 text-neutral-300 hover:text-primary transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Best Sellers
                </Link>
                <Link
                  to="/products?on_sale=true"
                  className="block py-2 text-neutral-300 hover:text-primary transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Today's Deals
                </Link>
              </div>
            </div>

            {/* Categories */}
            <div className="mb-6">
              <h3 className="font-semibold text-white mb-3">Categories</h3>
              <div className="space-y-1">
                {displayCategories.map((category) => (
                  <div
                    key={category.id}
                    className="border-b border-neutral-800 last:border-b-0"
                  >
                    <div className="w-full flex items-center justify-between py-3">
                      <Link
                        to={navigateToCategory(category.id)}
                        className="flex items-center space-x-3 flex-1 text-neutral-300 hover:text-primary transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {category.icon_path ? (
                          <div className="w-8 h-8 shrink-0 bg-neutral-800 rounded-md p-1">
                            <img
                              src={
                                getSupabaseCategoryImageUrl(
                                  category.icon_path,
                                ) || DEFAULT_CATEGORY_ICON
                              }
                              alt={category.name}
                              className="w-full h-full object-contain"
                            />
                          </div>
                        ) : (
                          <div className="w-8 h-8 shrink-0 bg-linear-to-br from-primary-dark to-primary rounded-md flex items-center justify-center">
                            <span className="text-sm font-bold text-black">
                              {category.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                        )}
                        <span className="font-medium">{category.name}</span>
                      </Link>
                      {category.sub_categories &&
                        category.sub_categories.length > 0 && (
                          <button
                            className="p-1 text-neutral-500 hover:text-white transition-colors"
                            onClick={() => {
                              setActiveCategory(
                                activeCategory === category.id.toString()
                                  ? null
                                  : category.id.toString(),
                              );
                            }}
                          >
                            <ChevronDown
                              className={`w-4 h-4 transition-transform ${
                                activeCategory === category.id.toString()
                                  ? "rotate-180"
                                  : ""
                              }`}
                            />
                          </button>
                        )}
                    </div>

                    {/* Subcategories */}
                    {activeCategory === category.id.toString() &&
                      category.sub_categories &&
                      category.sub_categories.length > 0 && (
                        <div className="pl-11 pb-2 space-y-1">
                          {category.sub_categories.map((subcat) => (
                            <Link
                              key={subcat.id}
                              to={navigateToSubCategory(category.id, subcat.id)}
                              className="block py-2 text-sm text-neutral-400 hover:text-primary transition-colors"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {subcat.name}
                            </Link>
                          ))}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>

            {/* User Actions */}
            {user ? (
              <div className="pt-4 border-t border-neutral-800">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-linear-to-br from-primary-dark to-primary rounded-full flex items-center justify-center text-black font-semibold text-sm shadow-md">
                    {(
                      user.user_metadata?.first_name?.charAt(0) ||
                      user.user_metadata?.full_name?.charAt(0) ||
                      user.email?.charAt(0)
                    )?.toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium text-white">
                      {user.user_metadata?.full_name ||
                        user.user_metadata?.first_name ||
                        user.email?.split("@")[0] ||
                        "User"}
                    </div>
                    <div className="text-sm text-neutral-500">{user.email}</div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    signOut();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-2 py-2 text-neutral-300 hover:text-red-500 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            ) : (
              <div className="pt-4 border-t border-neutral-800">
                <Link
                  to="/registration"
                  className="block w-full py-3 px-4 bg-linear-to-r from-primary-dark to-primary text-black font-semibold text-center rounded-lg hover:scale-105 transition-all duration-200 shadow-md"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign In / Register
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileHeader;
